import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, MapPin, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo, aboutInfo } from '../mock';

export const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About Drive Inverclyde
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Professional driving instruction with a personal touch. Learn from an experienced, 
              qualified instructor who is committed to your success.
            </p>
          </div>
        </div>
      </section>

      {/* Instructor Profile */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Instructor Information */}
            <div>
              <div className="bg-red-600 text-white px-4 py-2 rounded-full w-fit mb-4 text-sm font-semibold">
                DVSA QUALIFIED INSTRUCTOR
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Meet {aboutInfo.instructorName}, Your Driving Instructor
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {aboutInfo.philosophy}
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">{aboutInfo.qualification}</h4>
                    <p className="text-gray-600">{aboutInfo.experience}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Local Area Expertise</h4>
                    <p className="text-gray-600">Extensive knowledge of Inverclyde roads and test routes</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">High Success Rate</h4>
                    <p className="text-gray-600">Proven track record of first-time pass successes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Specializations Card */}
          <div className="mt-12">
            <div className="bg-white rounded-lg shadow-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Specializations</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {aboutInfo.specializations.map((spec, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
                    <Award className="w-5 h-5 text-red-600" />
                    <span className="text-gray-800 font-medium">{spec}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-4">Quick Facts</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Lesson Price:</span>
                    <span className="font-semibold text-red-600">£40 per hour</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transmission:</span>
                    <span className="font-semibold">Automatic</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Booking:</span>
                    <span className="font-semibold">Flexible scheduling</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Coverage:</span>
                    <span className="font-semibold">Inverclyde area</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Vehicles */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Learning Vehicle</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Learn in a modern, well-maintained automatic vehicle equipped with all the latest safety features
            </p>
          </div>
          
          {/* Automatic Vehicle */}
          <div className="mb-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Automatic Lessons</h3>
              <p className="text-gray-600">Learn automatic transmission with our Toyota Yaris</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              {/* Automatic Vehicle Photo */}
              <div className="order-1 lg:order-1">
                <img 
                  src={aboutInfo.vehicles.automatic.photo} 
                  alt="Toyota Yaris Automatic Driving School Car - Drive Inverclyde"
                  className="w-full rounded-2xl shadow-2xl object-cover"
                />
              </div>
              
              {/* Automatic Vehicle Information */}
              <div className="order-2 lg:order-2">
                <div className="bg-blue-600 text-white px-4 py-2 rounded-full w-fit mb-4 text-sm font-semibold">
                  AUTOMATIC TRANSMISSION
                </div>
                <h4 className="text-3xl font-bold text-gray-900 mb-6">
                  {aboutInfo.vehicles.automatic.make}
                </h4>
                
                {/* Vehicle Specs */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h5 className="font-semibold text-gray-900 mb-4">Vehicle Specifications</h5>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Engine:</span>
                      <span className="font-semibold">{aboutInfo.vehicles.automatic.engine}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Transmission:</span>
                      <span className="font-semibold">{aboutInfo.vehicles.automatic.transmission}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Power:</span>
                      <span className="font-semibold">{aboutInfo.vehicles.automatic.power}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Fuel Type:</span>
                      <span className="font-semibold">Hybrid</span>
                    </div>
                  </div>
                </div>
                
                {/* Vehicle Features */}
                <div>
                  <h5 className="font-semibold text-gray-900 mb-4">Learning Features</h5>
                  <div className="space-y-3">
                    {aboutInfo.vehicles.automatic.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-12 p-6 bg-blue-50 rounded-lg border-l-4 border-blue-600 max-w-4xl mx-auto">
            <p className="text-blue-800 text-lg">
              <strong>Perfect for all learners:</strong> Our automatic vehicle offers excellent visibility, 
              responsive handling, and modern safety features that help build confidence on the road.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Drive Inverclyde?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We combine professional expertise with a personal approach to help you become a confident, safe driver.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 pb-6">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-semibold text-lg mb-3">DVSA Qualified</h3>
                <p className="text-gray-600 text-sm">
                  Fully qualified and approved driving instructor with up-to-date training
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 pb-6">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-semibold text-lg mb-3">Patient Approach</h3>
                <p className="text-gray-600 text-sm">
                  Supportive teaching style that builds confidence and reduces anxiety
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 pb-6">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-semibold text-lg mb-3">Local Knowledge</h3>
                <p className="text-gray-600 text-sm">
                  Extensive knowledge of local roads, test routes, and driving conditions
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 pb-6">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-semibold text-lg mb-3">Flexible Hours</h3>
                <p className="text-gray-600 text-sm">
                  Lessons available to fit around work, school, and personal commitments
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Coverage Areas */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Service Areas</h2>
            <p className="text-gray-600">
              Professional driving instruction across the Inverclyde region and surrounding areas
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {businessInfo.coverageAreas.map((area, index) => (
                <div key={index} className="text-center">
                  <div className="bg-red-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                    <MapPin className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900">{area}</h3>
                  <p className="text-gray-600 text-sm mt-1">
                    {index < 3 ? 'Full coverage area' : 'Contact for availability'}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="mt-8 pt-8 border-t border-gray-200 text-center">
              <p className="text-gray-600 mb-4">
                Not sure if we cover your area? Get in touch and we'll let you know!
              </p>
              <Link to="/contact">
                <Button className="bg-red-600 hover:bg-red-700 text-white">
                  Check Coverage
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Approach */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Teaching Approach</h2>
              <p className="text-gray-600">
                Every student is unique, so we tailor our instruction to match your learning style and pace
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="border-l-4 border-l-red-600">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-xl mb-3 text-gray-900">Structured Learning</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We follow a progressive curriculum that builds skills systematically, ensuring you master 
                    each aspect of driving before moving to the next level.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-red-600">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-xl mb-3 text-gray-900">Confidence Building</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our patient, encouraging approach helps nervous learners overcome anxiety and build 
                    the confidence needed for safe, independent driving.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-red-600">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-xl mb-3 text-gray-900">Test Preparation</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Comprehensive test preparation including mock tests, route familiarization, 
                    and specific practice on challenging maneuvers.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-red-600">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-xl mb-3 text-gray-900">Ongoing Support</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Continuous feedback and progress tracking ensure you're always improving and 
                    know exactly what to focus on next.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Start Your Driving Journey Today</h2>
          <p className="text-xl mb-8 text-red-100">
            Ready to learn from an experienced, qualified instructor who cares about your success?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold">
                Book Your First Lesson
              </Button>
            </Link>
            <a href={`tel:${businessInfo.phone}`}>
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-semibold">
                Call: {businessInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;